import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, ArrowRight, Phone, KeyRound, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { errorMessage } from '../../api/client';
import { BRAND_NAME } from '../../utils/brand';
import PasswordChecklist from '../../components/PasswordChecklist';
import {
  isStrongPassword,
  PASSWORD_HELPER,
  PASSWORD_WEAK_MSG,
  PASSWORD_MISMATCH_MSG,
} from '../../utils/passwordPolicy';
import { trackOtpVerified } from '../../utils/analytics';

const MIN_LOCAL_DIGITS = 9;
const MAX_LOCAL_DIGITS = 10;

// USER forgot-password — phone → OTP → new password. Distinct from the
// existing email-token flow at /forgot-password which is used by staff /
// admin / vendor accounts.
function UserForgotPassword() {
  const { forgotPasswordOtp, resetPasswordOtp } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Preserve `?from=` deep-link context (e.g. `from=/referrals` when the
  // user got here via the "Get my link" flow). After the password is reset
  // + auto-signin we land on `from` instead of the default dashboard.
  const fromParam = params.get('from');

  const [step, setStep] = useState('phone'); // 'phone' | 'reset'
  const [localDigits, setLocalDigits] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [notRegistered, setNotRegistered] = useState(false);
  const otpRef = useRef(null);

  useEffect(() => {
    if (step === 'reset' && otpRef.current) otpRef.current.focus();
  }, [step]);

  function handleDigits(e) {
    let v = e.target.value.replace(/\D+/g, '');
    if (v.startsWith('0')) v = v.slice(1);
    setLocalDigits(v.slice(0, MAX_LOCAL_DIGITS));
  }

  function fullPhone() {
    return `+60${localDigits}`;
  }

  async function onSendOtp(e) {
    e.preventDefault();
    setErr('');
    setNotRegistered(false);
    if (localDigits.length < MIN_LOCAL_DIGITS) {
      setErr(`Enter a valid Malaysian mobile number (${MIN_LOCAL_DIGITS}–${MAX_LOCAL_DIGITS} digits after +60).`);
      return;
    }
    setBusy(true);
    try {
      await forgotPasswordOtp(fullPhone());
      toast.success(`OTP sent to ${fullPhone()}`);
      setStep('reset');
    } catch (e1) {
      // Backend returns 404 "No account found for that number." — instead
      // of a raw error, surface a friendly signup CTA. Any other error
      // (rate limit, network, etc.) falls through to the inline banner.
      if (e1?.response?.status === 404) {
        setNotRegistered(true);
      } else {
        setErr(errorMessage(e1));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onReset(e) {
    e.preventDefault();
    setErr('');
    if (otp.length < 4) {
      setErr('Enter the 4-digit OTP we sent to your phone.');
      return;
    }
    if (!isStrongPassword(password)) {
      setErr(PASSWORD_WEAK_MSG);
      return;
    }
    if (password !== confirmPassword) {
      setErr(PASSWORD_MISMATCH_MSG);
      return;
    }
    setBusy(true);
    try {
      await resetPasswordOtp(fullPhone(), otp, password);
      toast.success('Password updated');
      // GA4 conversion event — reset-flow OTP verified successfully.
      trackOtpVerified({ purpose: 'reset' });
      navigate(fromParam || '/dashboard', { replace: true });
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setErr('');
    setBusy(true);
    try {
      await forgotPasswordOtp(fullPhone());
      toast.success('OTP resent');
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setBusy(false);
    }
  }

  const registerHref = `/register?phone=${encodeURIComponent(localDigits)}${fromParam ? `&from=${encodeURIComponent(fromParam)}` : ''}`;

  return (
    <div className="auth-card">
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
        <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
      </div>

      {notRegistered && (
        <>
          <h2>Number not registered</h2>
          <div className="auth-sub" style={{ marginBottom: 'var(--s-3)' }}>
            <strong>{fullPhone()}</strong> isn't linked to any account yet. Want to sign up?
          </div>

          <Link to={registerHref} className="btn btn-primary btn-block" style={{ marginTop: 'var(--s-3)' }}>
            <ArrowRight size={16} /> Create an account
          </Link>

          <button
            type="button"
            onClick={() => { setNotRegistered(false); setErr(''); }}
            className="btn btn-outline btn-block"
            style={{ marginTop: 'var(--s-3)' }}
          >
            <ArrowLeft size={14} /> Try a different number
          </button>

          <div className="divider" />
          <div className="text-sm muted" style={{ textAlign: 'center' }}>
            <Link to="/login">Back to sign in</Link>
          </div>
        </>
      )}

      {!notRegistered && step === 'phone' && (
        <>
          <h2>Reset password</h2>
          <div className="auth-sub" style={{ marginBottom: 'var(--s-3)' }}>
            Enter the phone number on your account. We'll send you a one-time code to reset your password.
          </div>

          <form onSubmit={onSendOtp}>
            <div className="field">
              <label>Mobile number</label>
              <div
                className="row"
                style={{
                  border: '1px solid var(--border)', borderRadius: 8,
                  padding: '8px 12px', background: 'var(--surface)', gap: 8,
                }}
              >
                <span style={{ color: 'var(--ink-600)', fontWeight: 600 }}>+60</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="tel"
                  value={localDigits}
                  onChange={handleDigits}
                  placeholder="123456789"
                  style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 16 }}
                  required
                />
              </div>
            </div>

            {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}

            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? <span className="spinner" /> : <Phone size={16} />}
              {busy ? 'Sending…' : 'Send OTP'}
            </button>
          </form>

          <div className="divider" />
          <div className="text-sm muted" style={{ textAlign: 'center' }}>
            <Link to="/login">Back to sign in</Link>
          </div>
        </>
      )}

      {!notRegistered && step === 'reset' && (
        <>
          <h2>Set a new password</h2>
          <div className="auth-sub" style={{ marginBottom: 'var(--s-3)' }}>
            We sent a code to <strong>{fullPhone()}</strong>.
          </div>

          <form onSubmit={onReset}>
            <div className="field">
              <label>OTP</label>
              <input
                ref={otpRef}
                className="input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D+/g, ''))}
                placeholder="1111"
                style={{ letterSpacing: 4, fontSize: 18, textAlign: 'center' }}
                required
              />
            </div>

            <div className="field">
              <label>New password</label>
              <div
                className="row"
                style={{
                  border: '1px solid var(--border)', borderRadius: 8,
                  padding: '8px 12px', background: 'var(--surface)', gap: 8,
                }}
              >
                <input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your new password"
                  style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 16 }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--ink-500)' }}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="muted text-xs" style={{ marginTop: 6 }}>{PASSWORD_HELPER}</div>
              <PasswordChecklist password={password} />
            </div>

            <div className="field">
              <label>Confirm new password</label>
              <input
                className="input"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                required
              />
              {confirmPassword && confirmPassword !== password && (
                <div className="muted text-xs" style={{ marginTop: 6, color: 'var(--danger, #dc2626)' }}>
                  {PASSWORD_MISMATCH_MSG}
                </div>
              )}
            </div>

            {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}

            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? <span className="spinner" /> : <KeyRound size={16} />}
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>

          <div className="row between" style={{ marginTop: 'var(--s-4)' }}>
            <button
              type="button"
              onClick={() => { setStep('phone'); setOtp(''); setPassword(''); setConfirmPassword(''); setErr(''); }}
              style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--brand-700)' }}
              className="text-sm"
            >
              <ArrowLeft size={14} /> Change number
            </button>
            <button
              type="button"
              onClick={onResend}
              disabled={busy}
              style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--brand-700)' }}
              className="text-sm"
            >
              Resend code <ArrowRight size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default UserForgotPassword;
